import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState, type ReactNode } from 'react';
import aiExplanationStep1 from '@/assets/images/ai-explanation-step-1.webp';
import aiExplanationStep2 from '@/assets/images/ai-explanation-step-2.webp';
import aiExplanationStep3 from '@/assets/images/ai-explanation-step-3.webp';
import { CpuSetting, EyeSlash, UserTick } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { DialogClose } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { ModalOptions } from '@/components/ui/modal-provider';

/*  Types                                                              */

interface SecurityTopic {
    id: string;
    icon: ReactNode;
    title: string;
    summary: string;
    details: string[];
    /** Illustration for the step, shown above the title. All are 949×636. */
    image?: string;
    animation?: ReactNode;
    extraContent?: ReactNode;
}

const TOPICS: SecurityTopic[] = [
    {
        id: 'ai-triggered',
        icon: <CpuSetting className="size-5" variant="Bulk" />,
        title: 'A IA é acionada',
        summary:
            'Antes de qualquer texto chegar à IA, nosso sistema remove automaticamente nomes, CPFs, endereços e qualquer dado que identifique seu paciente.',
        image: aiExplanationStep1,
        details: [
            'A detecção é feita inteiramente nos nossos servidores. Nenhum dado identificável sai para serviços externos durante esse processo.',
        ],
    },
    {
        id: 'ai-blind',
        icon: <EyeSlash className="size-5" variant="Bulk" />,
        title: 'A IA trabalha às cegas',
        summary:
            'A IA recebe apenas conteúdo clínico anonimizado. Ela não sabe quem é seu paciente, de onde é, nem quem é você.',
        image: aiExplanationStep2,
        details: [
            'Cada geração é independente. A IA não tem memória entre sessões e não cruza dados de outros pacientes.',
        ],
    },
    {
        id: 'back-to-you',
        icon: <UserTick className="size-5" variant="Bulk" />,
        title: 'O texto volta para você',
        summary:
            'Os dados reais são restaurados no seu editor. Você revisa, edita e decide o que fica. Nada entra no laudo sem a sua aprovação.',
        image: aiExplanationStep3,
        details: [],
    },
];

const contentVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -6 },
};

/*  Gap between timeline nodes (must match the parent's gap utility).
    Desktop: gap-5 = 1.25rem = 20px
    Mobile:  gap-3 = 0.75rem = 12px                                    */
const DESKTOP_GAP_PX = 20;
const MOBILE_GAP_PX = 12;

// eslint-disable-next-line react-refresh/only-export-components
function TimelineNode({
    topic,
    index,
    activeIndex,
    isLast,
    mounted,
    onClick,
}: {
    topic: SecurityTopic;
    index: number;
    activeIndex: number;
    isLast: boolean;
    mounted: boolean;
    onClick: () => void;
}) {
    const isActive = index === activeIndex;
    const isPast = index < activeIndex;
    const connectorIsPast = index < activeIndex;

    return (
        <motion.div
            className="relative flex items-center gap-3 md:items-start"
            initial={mounted ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
                delay: mounted ? 0 : index * 0.07,
                duration: 0.35,
                ease: [0.25, 0.46, 0.45, 0.94],
            }}
        >
            {/* Connector line — horizontal on mobile, vertical on desktop */}
            {!isLast && (
                <>
                    {/* Desktop: vertical connector */}
                    <div
                        className={cn(
                            'absolute left-[16px] top-[36px] hidden w-[2px] rounded-full transition-colors duration-500 md:block',
                            connectorIsPast ? 'bg-blue-300' : 'bg-muted/80',
                        )}
                        style={{ height: `calc(100% + ${DESKTOP_GAP_PX}px)` }}
                        aria-hidden="true"
                    />
                    {/* Mobile: horizontal connector */}
                    <div
                        className={cn(
                            'absolute left-[36px] top-[16px] block h-[2px] rounded-full transition-colors duration-500 md:hidden',
                            connectorIsPast ? 'bg-blue-300' : 'bg-muted/80',
                        )}
                        style={{ width: `calc(100% + ${MOBILE_GAP_PX}px)` }}
                        aria-hidden="true"
                    />
                </>
            )}

            {/* Node circle */}
            <motion.button
                key={isActive ? 'active' : 'idle'}
                type="button"
                onClick={onClick}
                initial={isActive ? { scale: 0.92 } : false}
                animate={{ scale: 1 }}
                transition={
                    isActive ? { duration: 0.35, ease: [0.34, 1.56, 0.64, 1] } : { duration: 0 }
                }
                className={cn(
                    'relative z-10 flex size-[34px] shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-300',
                    isActive &&
                        'border-blue-500 bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30',
                    isPast && !isActive && 'border-blue-200 bg-blue-50 text-blue-500',
                    !isPast &&
                        !isActive &&
                        'border-border bg-card text-muted-foreground hover:border-blue-300 hover:text-blue-400',
                )}
                aria-label={`${index + 1}. ${topic.title}`}
                aria-current={isActive ? 'step' : undefined}
            >
                {/* Active pulse ring */}
                <AnimatePresence>
                    {isActive && (
                        <motion.span
                            className="absolute inset-0 rounded-full border-2 border-blue-400"
                            initial={{ opacity: 0.6, scale: 1 }}
                            animate={{ opacity: 0, scale: 1.6 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: 1.8,
                                repeat: Infinity,
                                ease: 'easeOut',
                            }}
                            aria-hidden="true"
                        />
                    )}
                </AnimatePresence>
                <span className="flex size-4 items-center justify-center">{topic.icon}</span>
            </motion.button>

            {/* Label — hidden on mobile, visible on desktop */}
            <button
                type="button"
                onClick={onClick}
                className={cn(
                    'hidden pt-1.5 text-left text-[13px] leading-tight transition-colors duration-300 md:block',
                    isActive && 'font-semibold text-foreground',
                    isPast && !isActive && 'font-medium text-muted-foreground',
                    !isPast &&
                        !isActive &&
                        'font-medium text-muted-foreground hover:text-muted-foreground',
                )}
            >
                {topic.title}
            </button>
        </motion.div>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
function TopicDetail({ topic }: { topic: SecurityTopic }) {
    return (
        <motion.div
            key={topic.id}
            variants={contentVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="flex h-full flex-col"
        >
            {/* Illustration for the step. Sized off the content column so it
                shrinks with the modal on mobile, capped so it stays a hero and
                not a masthead on desktop. */}
            {topic.image && (
                <div className="mb-5 flex justify-center">
                    <img
                        src={topic.image}
                        alt=""
                        // Intrinsic size, so the body reserves the box instead
                        // of reflowing when the next step's art decodes.
                        width={949}
                        height={636}
                        decoding="async"
                        className="h-auto w-full max-w-[340px]"
                    />
                </div>
            )}

            {/* Animation slot — drop Lottie components here */}
            {topic.animation && (
                <div className="mb-5 flex items-center justify-center">{topic.animation}</div>
            )}

            {/* Title. No icon tile beside it — the illustration above carries
                the step now, and the timeline node on the left already holds
                the glyph. */}
            <h3 className="mb-2 text-[15px] font-bold text-foreground">{topic.title}</h3>

            {/* Summary */}
            <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">
                {topic.summary}
            </p>

            {/* Detail bullets — editorial dot style */}
            {topic.details.length > 0 && (
                <ul className="space-y-3">
                    {topic.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                            <span
                                className={cn(
                                    'mt-[7px] size-1.5 shrink-0 rounded-full',
                                    'bg-blue-400',
                                )}
                                aria-hidden="true"
                            />
                            <span className="text-[13px] leading-relaxed text-muted-foreground">
                                {detail}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {/* Extra content (e.g. privacy link) */}
            {topic.extraContent && <div className="mt-auto pt-5">{topic.extraContent}</div>}
        </motion.div>
    );
}

/*  Main content component                                             */

// eslint-disable-next-line react-refresh/only-export-components
function AISecurityInfoContent() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [mounted, setMounted] = useState(false);
    const activeTopic = TOPICS[activeIndex];

    // After entrance animations have played, skip them on re-renders
    useEffect(() => {
        const timer = setTimeout(() => setMounted(true), 500);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="flex h-full flex-col md:flex-row">
            {/* Timeline panel —
                Mobile:  horizontal stepper row on top
                Desktop: vertical sidebar on the left */}
            <nav
                className={cn(
                    'flex shrink-0 items-center justify-center gap-3 border-b border-border/80 bg-gradient-to-r from-surface via-surface to-blue-50/60 p-4',
                    'md:w-52 md:flex-col md:items-stretch md:justify-start md:gap-5 md:border-b-0 md:border-r md:bg-gradient-to-b md:px-0 md:py-6 md:pl-6 md:pr-4',
                )}
                aria-label="Jornada dos seus dados"
            >
                {TOPICS.map((topic, index) => (
                    <TimelineNode
                        key={topic.id}
                        topic={topic}
                        index={index}
                        activeIndex={activeIndex}
                        isLast={index === TOPICS.length - 1}
                        mounted={mounted}
                        onClick={() => setActiveIndex(index)}
                    />
                ))}
            </nav>

            {/* Content panel */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-5 md:p-6">
                <AnimatePresence mode="wait">
                    <TopicDetail topic={activeTopic} />
                </AnimatePresence>
            </div>
        </div>
    );
}

export function getAISecurityInfoModalOptions(): ModalOptions {
    return {
        title: 'A jornada dos seus dados',
        description:
            'Veja o que acontece em cada etapa. Do momento que você digita até a IA responder.',
        content: <AISecurityInfoContent />,
        size: 'lg',
        footer: (
            <DialogClose asChild>
                <Button variant="default">Entendi</Button>
            </DialogClose>
        ),
    };
}
